import { useEffect, useRef, useState } from "react";

/**
 * Hook that manages the "begin together" → "complete" state for a single habit.
 * - startedAt is persisted per (userId, templateId, YYYY-MM-DD) in localStorage
 *   so that a page refresh mid-run does not reset the timer.
 * - While a habit is running, we request navigator.wakeLock so the screen stays on.
 * - completeEnabled flips to true once (startedAt + durationMin*60_000) is in the past.
 */
export function useHabitSession({ userId, templateId, durationMin }) {
  const key = userId && templateId
    ? `cadence.session.${userId}.${templateId}.${new Date().toISOString().slice(0, 10)}`
    : null;

  const [startedAt, setStartedAt] = useState(() => {
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return typeof parsed?.startedAt === "number" ? parsed.startedAt : null;
    } catch { return null; }
  });
  const [now, setNow] = useState(() => Date.now());
  const wakeLockRef = useRef(null);

  // 1-second tick while a session is active
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  // Screen wake lock while running
  useEffect(() => {
    const shouldHold = !!startedAt && durationMin && (Date.now() - startedAt) < durationMin * 60_000;
    let cancelled = false;

    const acquire = async () => {
      if (!("wakeLock" in navigator)) return;
      try {
        const wl = await navigator.wakeLock.request("screen");
        if (cancelled) { wl.release?.(); return; }
        wakeLockRef.current = wl;
        wl.addEventListener?.("release", () => { wakeLockRef.current = null; });
      } catch { /* wake lock denied — ignore */ }
    };
    const release = () => {
      wakeLockRef.current?.release?.().catch(() => {});
      wakeLockRef.current = null;
    };

    if (shouldHold) acquire();
    else release();

    const onVis = () => {
      if (document.visibilityState === "visible" && shouldHold && !wakeLockRef.current) {
        acquire();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      release();
    };
  }, [startedAt, durationMin, now]);

  const begin = () => {
    if (!key) return;
    const ts = Date.now();
    try { localStorage.setItem(key, JSON.stringify({ startedAt: ts })); } catch { /* noop */ }
    setStartedAt(ts);
    setNow(ts);
  };

  const clear = () => {
    if (!key) return;
    try { localStorage.removeItem(key); } catch { /* noop */ }
    setStartedAt(null);
  };

  const endsAt = startedAt && durationMin ? startedAt + durationMin * 60_000 : null;
  const isRunning = !!startedAt && endsAt != null && now < endsAt;
  const isReadyToComplete = !!startedAt && endsAt != null && now >= endsAt;
  const remainingMs = endsAt ? Math.max(0, endsAt - now) : 0;
  const progressPct = startedAt && endsAt
    ? Math.min(100, Math.max(0, ((now - startedAt) / (endsAt - startedAt)) * 100))
    : 0;

  return {
    startedAt,
    isRunning,
    isReadyToComplete,
    remainingMs,
    progressPct,
    begin,
    clear,
  };
}

export function formatMs(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
